<template>
  <FlotoContentLoader :loading="loading">
    <div class="flex flex-col flex-1">
      <MRow class="my-5 items-center">
        <MCol :size="2">
          <BoxNode
            :title="`${$tc('total')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.totalAmount"
          />
        </MCol>
        <MCol :size="2">
          <BoxNode
            :title="`${$tc('purchase')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.purchaseAmount"
          />
        </MCol>
        <MCol :size="2">
          <BoxNode
            :title="`${$tc('operation')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.operationalAmount"
          />
        </MCol>
        <MCol :size="2">
          <BoxNode
            :title="`${$tc('disposal')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.disposalAmount"
          />
        </MCol>
        <MCol :size="2">
          <BoxNode
            :title="`${$tc('other')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.othersAmount"
          />
        </MCol>
        <MCol v-if="moduleName !== $constants.ASSET_CONSUMABLE" :size="2">
          <BoxNode
            :title="`${$tc('current')} ${$tc('book')} ${$tc('value')}`"
            :shadow="false"
            :count="
              costInfo.bookValue || costInfo.bookValue === 0
                ? costInfo.bookValue
                : '---'
            "
          />
        </MCol>
        <MCol :size="2" class="mt-3">
          <BoxNode
            :title="`${$tc('repair')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.repairAmount"
          />
        </MCol>
        <!-- @TODO-1920 Add language key "upgrade" -->
        <MCol :size="2" class="mt-3">
          <BoxNode
            :title="`${$tc('upgrade')} ${$tc('cost')}`"
            :shadow="false"
            :count="costInfo.upgradeAmount"
          />
        </MCol>
      </MRow>
      <MDivider />
      <FlotoCrudContainer
        as-table
        prepend-new-item
        :scrollable="false"
        :fetch-fn="getFinancialCosts"
        :delete-fn="deleteFinancialCost"
        :create-fn="addFinancialCost"
        :update-fn="updateFinancialCost"
        :columns="tableColumns"
        :show-no-data="false"
      >
        <template v-if="!disabled" v-slot:add-controls="{ create }">
          <MRow class="mb-4" :gutter="0">
            <MCol :size="12" class="m-align-button">
              <MButton id="add-cost-btn" variant="primary" @click="create">
                {{ $t('add') }} {{ $tc('cost') }}
              </MButton>
            </MCol>
          </MRow>
        </template>
        <template v-slot:form-header="{ item }">
          {{ $t(item.id ? 'edit' : 'add') }} {{ $tc('cost') }}
        </template>
        <template v-slot:form-items="{ item: productType }">
          <CostForm :form-data="productType" :resource="resource" />
        </template>
        <template v-slot:form-actions="{ submit, cancel, item, processing }">
          <MButton
            id="add-update-btn"
            class="mx-1"
            :loading="processing"
            @click="submit"
          >
            {{ $t(item.id ? 'update' : 'add') }}
          </MButton>
          <MButton id="cancel-btn" variant="default" @click="cancel">
            {{ $t('cancel') }}
          </MButton>
        </template>
        <template v-slot:acquireDate="{ item, edit }">
          <td class="resource-link">
            <a @click="!disabled ? edit : undefined">
              <span v-if="resource.origin === 'purchase'">
                {{ item.acquireDate | datetime(undefined, false) }}
              </span>
              <span v-else>{{ item.acquireDate | datetime }}</span>
            </a>
          </td>
        </template>
        <template v-slot:amount="{ item }">
          <td class="text-ellipsis">
            {{ item.amount }}
          </td>
        </template>
        <template v-slot:currencyId="{ item }">
          <td class="text-ellipsis">
            <CurrencyPicker
              :value="item.currencyId"
              text-only
              disabled
              style="white-space: nowrap"
            />
          </td>
        </template>
        <template v-slot:costFactor="{ item }">
          <td class="text-ellipsis">
            {{ $tc(item.costFactor) }}
          </td>
        </template>

        <template v-slot:actions="{ item, remove, edit }">
          <td class="text-ellipsis">
            <MTooltip>
              <template v-slot:trigger>
                <a :disabled="disabled" @click="edit">
                  <MIcon name="pencil" class="cursor-pointer text-primary" />
                </a>
              </template>
              {{ $t('edit') }}
            </MTooltip>
            <FlotoDeleteBtn
              :disabled="disabled"
              class="mx-2"
              :class="{ invisible: !item.canDelete }"
              :message="$t('confirm_delete_item', { item: $tc('cost') })"
              @confirm="remove"
            />
          </td>
        </template>
      </FlotoCrudContainer>
    </div>
  </FlotoContentLoader>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import { OrganizationComputed } from '@state/modules/organization'
import BoxNode from '@modules/asset-management/components/discovery-component/discovery-service/box-node'
import {
  searchFinancialCostApi,
  deleteFinancialCostApi,
  updateFinancialCostApi,
  createFinancialCostApi,
} from '../../asset-api'
import CostForm from './cost-form'
export default {
  name: 'CostTab',
  components: { BoxNode, CostForm, CurrencyPicker },
  props: {
    resourceId: { type: Number, required: true },
    resource: { type: Object, required: true },
    moduleName: { type: String, required: true },
    loading: { type: Boolean, required: true },
    costInfo: {
      type: Object,
      default() {
        return {}
      },
    },
    disabled: { type: Boolean, default: false },
  },
  data() {
    this.tableColumns = [
      { name: `${this.$t('date')}`, key: 'acquireDate' },
      {
        name: `${this.$tc('amount')}`,
        key: 'amount',
      },
      {
        name: `${this.$tc('currency')}`,
        key: 'currencyId',
      },
      {
        name: `${this.$tc('cost')} ${this.$tc('factor')}`,
        key: 'costFactor',
      },
      {
        name: `${this.$t('description')}`,
        key: 'description',
      },
      {
        name: `${this.$tc('action', 2)}`,
        key: 'actions',
        width: '120px',
      },
    ]
    return {}
  },
  computed: {
    ...OrganizationComputed,
  },
  methods: {
    getFinancialCosts(limit, offset) {
      return searchFinancialCostApi(
        this.moduleName,
        this.resourceId,
        limit,
        offset
      )
    },
    deleteFinancialCost({ id }) {
      return deleteFinancialCostApi(this.moduleName, this.resourceId, id).then(
        (data) => {
          this.$emit('refresh')
          return data
        }
      )
    },
    updateFinancialCost(data) {
      return updateFinancialCostApi(
        this.moduleName,
        this.resourceId,
        data
      ).then((data) => {
        this.$emit('refresh')
        return data
      })
    },
    addFinancialCost(data) {
      return createFinancialCostApi(
        this.moduleName,
        this.resourceId,
        data
      ).then((data) => {
        this.$emit('refresh')
        return data
      })
    },
  },
}
</script>
